/**
 * IndexedDB Schema Definition for Offline-First Architecture
 *
 * This file defines the database schema, version management, and migration logic
 * for the offline-first implementation using IndexedDB.
 */

import type { TechniquePosition, TechniqueType, TechniqueDifficulty } from '@/types/technique';

export const DB_NAME = 'MatMonsterOfflineDB';
export const DB_VERSION = 6; // Incremented to add referral data stores

/**
 * Object Store Names
 */
export const STORES = {
  // Core data stores
  TRAINING_SESSIONS: 'training_sessions',
  TECHNIQUES: 'techniques',
  USER_TECHNIQUES: 'user_techniques',
  POSITIONS: 'positions',
  GAME_PLANS: 'game_plans',

  // Roadmap data stores
  ROADMAP_MACRO: 'roadmap_macro',
  ROADMAP_MESO: 'roadmap_meso',

  // User and profile data
  USER_PROFILE: 'user_profile',
  PERMISSIONS: 'permissions',

  // Weekly reports and insights
  WEEKLY_REPORTS: 'weekly_reports',

  // Static assets (logos, images)
  STATIC_ASSETS: 'static_assets',

  // Referral data
  REFERRAL_CODES: 'referral_codes',
  REFERRALS: 'referrals',
  REFERRAL_STATS: 'referral_stats',

  // Sync and metadata
  SYNC_QUEUE: 'sync_queue',
  SYNC_METADATA: 'sync_metadata',
  LAST_SYNC: 'last_sync',

  // Offline state
  OFFLINE_ACTIONS: 'offline_actions',
  CONFLICT_RESOLUTION: 'conflict_resolution'
} as const;

/**
 * Index Definitions for each store
 */
export const INDEXES = {
  [STORES.TRAINING_SESSIONS]: [
    { name: 'by_user_id', keyPath: 'user_id' },
    { name: 'by_date', keyPath: 'date' },
    { name: 'by_user_date', keyPath: ['user_id', 'date'] },
    { name: 'by_updated_at', keyPath: 'updated_at' },
    { name: 'by_session_type', keyPath: 'session_type' }
  ],
  [STORES.TECHNIQUES]: [
    { name: 'by_position_id', keyPath: 'position_id' },
    { name: 'by_technique_type', keyPath: 'technique_type' },
    { name: 'by_difficulty', keyPath: 'difficulty' },
    { name: 'by_is_admin_created', keyPath: 'is_admin_created' },
    { name: 'by_created_by_user_id', keyPath: 'created_by_user_id' },
    { name: 'by_updated_at', keyPath: 'updated_at' }
  ],
  [STORES.USER_TECHNIQUES]: [
    { name: 'by_user_id', keyPath: 'user_id' },
    { name: 'by_technique_id', keyPath: 'technique_id' },
    { name: 'by_user_technique', keyPath: ['user_id', 'technique_id'] },
    { name: 'by_is_favorite', keyPath: 'is_favorite' },
    { name: 'by_last_practiced', keyPath: 'last_practiced' }
  ],
  [STORES.POSITIONS]: [
    { name: 'by_name', keyPath: 'name' },
    { name: 'by_category', keyPath: 'category' }
  ],
  [STORES.GAME_PLANS]: [
    { name: 'by_user_id', keyPath: 'user_id' },
    { name: 'by_is_public', keyPath: 'is_public' },
    { name: 'by_is_template', keyPath: 'is_template' },
    { name: 'by_is_admin_template', keyPath: 'is_admin_template' },
    { name: 'by_updated_at', keyPath: 'updated_at' }
  ],
  [STORES.ROADMAP_MACRO]: [
    { name: 'by_belt_color', keyPath: 'belt_color' },
    { name: 'by_stripe_count', keyPath: 'stripe_count' },
    { name: 'by_belt_stripe', keyPath: ['belt_color', 'stripe_count'] },
    { name: 'by_order_index', keyPath: 'order_index' },
    { name: 'by_is_active', keyPath: 'is_active' }
  ],
  [STORES.ROADMAP_MESO]: [
    { name: 'by_belt_color', keyPath: 'belt_color' },
    { name: 'by_stripe_count', keyPath: 'stripe_count' },
    { name: 'by_belt_stripe', keyPath: ['belt_color', 'stripe_count'] },
    { name: 'by_order_index', keyPath: 'order_index' },
    { name: 'by_is_active', keyPath: 'is_active' }
  ],
  [STORES.SYNC_QUEUE]: [
    { name: 'by_table_name', keyPath: 'table_name' },
    { name: 'by_operation', keyPath: 'operation' },
    { name: 'by_priority', keyPath: 'priority' },
    { name: 'by_created_at', keyPath: 'created_at' },
    { name: 'by_retry_count', keyPath: 'retry_count' },
    { name: 'by_status', keyPath: 'status' }
  ],
  [STORES.SYNC_METADATA]: [
    { name: 'by_table_name', keyPath: 'table_name' },
    { name: 'by_last_sync', keyPath: 'last_sync' }
  ],
  [STORES.OFFLINE_ACTIONS]: [
    { name: 'by_action_type', keyPath: 'action_type' },
    { name: 'by_created_at', keyPath: 'created_at' },
    { name: 'by_status', keyPath: 'status' }
  ],
  [STORES.PERMISSIONS]: [
    { name: 'by_user_id', keyPath: 'user_id' },
    { name: 'by_cached_at', keyPath: 'cached_at' },
    { name: 'by_expires_at', keyPath: 'expires_at' }
  ],
  [STORES.WEEKLY_REPORTS]: [
    { name: 'by_user_id', keyPath: 'user_id' },
    { name: 'by_week_start', keyPath: 'week_start' },
    { name: 'by_user_week', keyPath: ['user_id', 'week_start'] },
    { name: 'by_created_at', keyPath: 'created_at' },
    { name: 'by_has_training', keyPath: 'has_training' },
    { name: 'by_insight_type', keyPath: 'insight_type' }
  ],
  [STORES.STATIC_ASSETS]: [
    { name: 'by_asset_type', keyPath: 'asset_type' },
    { name: 'by_cached_at', keyPath: 'cached_at' },
    { name: 'by_expires_at', keyPath: 'expires_at' }
  ],
  [STORES.REFERRAL_CODES]: [
    { name: 'by_user_id', keyPath: 'user_id' },
    { name: 'by_code', keyPath: 'code' },
    { name: 'by_created_at', keyPath: 'created_at' }
  ],
  [STORES.REFERRALS]: [
    { name: 'by_referrer_id', keyPath: 'referrer_id' },
    { name: 'by_referred_user_id', keyPath: 'referred_user_id' },
    { name: 'by_status', keyPath: 'status' },
    { name: 'by_created_at', keyPath: 'created_at' }
  ],
  [STORES.REFERRAL_STATS]: [
    { name: 'by_user_id', keyPath: 'user_id' },
    { name: 'by_cached_at', keyPath: 'cached_at' },
    { name: 'by_expires_at', keyPath: 'expires_at' }
  ]
} as const;

/**
 * Data Types for IndexedDB stores
 */
export interface TrainingSessionOffline {
  id: string;
  user_id: string;
  title: string;
  date: string;
  duration: number;
  location: string;
  session_type: string;
  techniques_practiced: string[];
  notes: string;
  belt_rank: string;
  stripe_count: number;
  professor: string;
  learned: string;
  improvements: string;
  successes: string;
  youtube_url: string;
  competition_finish: string;
  competition_matches: Array<{
    opponent?: string;
    result?: string;
    submission?: string;
    notes?: string;
  }>;
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface TechniqueOffline {
  id: string;
  name: string;
  slug: string;
  description: string;
  position_id: string;
  technique_position: TechniquePosition;
  technique_type: TechniqueType;
  difficulty: TechniqueDifficulty;
  image_url?: string;
  video_url?: string;
  key_points: string[];
  common_mistakes: string[];
  category?: string;
  subcategory?: string;
  is_admin_created: number; // Stored as 0/1 for IndexedDB compatibility
  is_user_created: number; // Stored as 0/1 for IndexedDB compatibility
  created_by_user_id?: string;
  is_private: number; // Stored as 0/1 for IndexedDB compatibility
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface UserTechniqueOffline {
  id: string;
  user_id: string;
  technique_id: string;
  notes?: string;
  proficiency_level: string; // Changed to string to match database
  last_practiced?: string;
  times_practiced: number;
  is_favorite: number; // Stored as 0/1 for IndexedDB compatibility
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface PositionOffline {
  id: string;
  name: string;
  description?: string;
  category?: string;
  parent_id?: string;
  created_at: string;
  updated_at: string;
}

export interface GamePlanOffline {
  id: string;
  name: string;
  description?: string;
  thumbnail?: string;
  flow_data: {
    nodes: Array<{
      id: string;
      type?: string;
      position?: { x: number; y: number };
      data?: Record<string, unknown>;
    }>;
    edges: Array<{
      id: string;
      source: string;
      target: string;
      type?: string;
      data?: Record<string, unknown>;
    }>;
    thumbnail?: string;
  };
  user_id: string;
  is_public: number; // Stored as 0/1 for IndexedDB compatibility
  is_template: number; // Stored as 0/1 for IndexedDB compatibility
  is_admin_template?: number; // Stored as 0/1 for IndexedDB compatibility
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface UserProfileOffline {
  id: string;
  username?: string;
  full_name?: string;
  avatar_url?: string;
  home_gym?: string;
  belt_rank?: string;
  stripes?: string;
  email_preferences: {
    weekly_summary: boolean;
    weekly_insights: boolean;
    last_updated: string;
  };
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface RoadmapMacroOffline {
  id: string;
  title: string;
  belt_color: string;
  stripe_count: number;
  content?: string;
  image_url?: string;
  video_url?: string;
  order_index: number;
  is_active: number; // Stored as 0/1 for IndexedDB compatibility
  created_at: string;
  updated_at: string;
  created_by?: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface RoadmapMesoOffline {
  id: string;
  title: string;
  belt_color: string;
  stripe_count: number;
  content?: string;
  image_url?: string;
  video_url?: string;
  key_techniques?: string[];
  learning_objectives?: string[];
  order_index: number;
  is_active: number; // Stored as 0/1 for IndexedDB compatibility
  created_at: string;
  updated_at: string;
  created_by?: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface SyncQueueItem {
  id: string;
  table_name: string;
  record_id: string;
  operation: 'create' | 'update' | 'delete';
  data?: Record<string, unknown>;
  priority: number; // 1 = highest, 10 = lowest
  retry_count: number;
  max_retries: number;
  created_at: string;
  last_attempt?: string;
  error_message?: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
}

export interface SyncMetadata {
  table_name: string;
  last_sync: string;
  last_full_sync?: string;
  sync_strategy: 'incremental' | 'full';
  record_count: number;
  last_error?: string;
}

export interface OfflineAction {
  id: string;
  action_type: string;
  data: Record<string, unknown>;
  created_at: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message?: string;
}

export interface ConflictResolution {
  id: string;
  table_name: string;
  record_id: string;
  local_data: Record<string, unknown>;
  remote_data: Record<string, unknown>;
  conflict_type: 'update_conflict' | 'delete_conflict' | 'create_conflict';
  created_at: string;
  resolved: boolean;
  resolution_strategy?: 'local_wins' | 'remote_wins' | 'manual' | 'custom' | 'merge' | 'timestamp_latest';
  resolved_at?: string;
}

export interface PermissionsOffline {
  id: string;
  user_id: string;
  is_authenticated: boolean;
  is_premium: boolean;
  is_admin: boolean;
  subscription_status: 'active' | 'inactive' | 'cancelled' | 'past_due' | 'trialing' | 'unpaid';
  subscription_source: 'stripe' | 'admin_override' | 'fallback';
  subscription_expires_at?: string;
  permissions_status: 'ready' | 'loading' | 'error';
  last_checked: number;
  cached_at: string;
  expires_at: string;
  // Additional offline-specific metadata
  cache_source: 'api' | 'fallback' | 'manual';
  invalidation_reason?: string;
  sync_required: boolean;
}

export interface WeeklyReportOffline {
  id: string;
  user_id: string;
  week_start: string;
  ai_insights?: string;
  summary?: string;
  email_status: {
    status: 'pending' | 'sent' | 'failed' | 'retry';
    attempts: number;
    last_attempt: string | null;
    error: string | null;
    sent_at: string | null;
  };
  feedback_rating?: string;
  has_training?: boolean;
  included_competition_data?: boolean;
  insight_type?: string;
  model_version?: string;
  recommendations?: unknown;
  technique_tags?: string[];
  training_count?: number;
  training_minutes?: number;
  competition_summary?: unknown;
  viewed_at?: string;
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

/**
 * Referral Data Offline Schemas
 */
export interface ReferralCodeOffline {
  id: string;
  user_id: string;
  code: string;
  visit_count: number;
  campaign_visits: Record<string, number>;
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface ReferralOffline {
  id: string;
  referrer_id: string;
  referred_user_id: string;
  source: string;
  status: string;
  created_at: string;
  updated_at: string;
  // Offline-specific fields
  _offline_created?: boolean;
  _offline_updated?: boolean;
  _sync_status?: 'pending' | 'synced' | 'conflict';
  _last_modified?: string;
}

export interface ReferralStatsOffline {
  id: string;
  user_id: string;
  total_referrals: string;
  successful_signups: string;
  premium_conversions: string;
  rewards_earned: string;
  progress_to_next_reward: number;
  recent_referrals: Array<{
    id: string;
    created_at: string;
    status: string;
  }>;
  cached_at: string;
  expires_at: string;
  // Required for BaseOperations compatibility
  updated_at: string;
  // Offline-specific fields
  _cache_version?: number;
  _last_validated?: string;
}

/**
 * Static Asset (Logo, Image) Offline Schema
 */
export interface StaticAssetOffline {
  id: string; // URL of the asset (primary key)
  asset_type: 'logo' | 'image' | 'icon';
  blob_data: Blob;
  mime_type: string;
  file_size: number;
  cached_at: string;
  expires_at: string;
  last_accessed?: string;
  // Metadata
  original_url: string;
  cache_key: string;
  // Required for BaseOperations compatibility
  updated_at: string;
  // Offline-specific fields
  _cache_version?: number;
  _last_validated?: string;
}

/**
 * Schema validation and migration utilities
 */
export interface SchemaVersion {
  version: number;
  stores: string[];
  migrations?: ((db: IDBDatabase, transaction: IDBTransaction) => void)[];
}

export const SCHEMA_VERSIONS: SchemaVersion[] = [
  {
    version: 1,
    stores: Object.values(STORES).filter(store => store !== 'weekly_reports' && store !== 'static_assets'),
    migrations: []
  },
  {
    version: 2,
    stores: Object.values(STORES).filter(store => store !== 'static_assets'),
    migrations: []
  },
  {
    version: 3,
    stores: Object.values(STORES),
    migrations: []
  },
  {
    version: 4,
    stores: Object.values(STORES),
    migrations: [] // Boolean to number conversion handled by data transformers
  },
  {
    version: 5,
    stores: Object.values(STORES).filter(store => !['referral_codes', 'referrals', 'referral_stats'].includes(store)),
    migrations: [] // Added missing sync_queue by_status index
  },
  {
    version: 6,
    stores: Object.values(STORES),
    migrations: [] // Added referral data stores
  }
];
